import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { useAuthContext } from "providers/Auth";
import { LeadSource } from "views/leads-id/types";
import { ContactFormsSubmenuLeadSource } from "./types";

export const useEligibleContactFormSources = ({
  enabled = true
}: { enabled?: boolean } = {}) => {
  // Per-source links mint tenant leads, so without tenant_leads there is nothing
  // to offer: every "Contact form" action on properties / units hides (CRM-759).
  const { type } = useAuthContext();
  const leadsEnabled = type.isTenantLeads;
  const { data, isLoading } = useQuery({
    queryKey: ["LEAD_SOURCES_LIST"],
    queryFn: () =>
      instance
        .get<{ items: LeadSource[] }>("/portal/v1/boom_crm/lead_sources")
        .then((res) => res.data),
    // Lead sources are near-static; without a staleTime the closed popover unmounts its
    // observer and every menu open refetches the whole list.
    staleTime: 5 * 60_000,
    // Callers on pages that render for non-CRM partners pass enabled: false so those
    // partners don't pay for a list they can never see.
    enabled: enabled && leadsEnabled
  });

  const eligibleSources = useMemo<ContactFormsSubmenuLeadSource[]>(
    () =>
      (leadsEnabled ? (data?.items ?? []) : [])
        .filter((source) => source.enable_contact_form)
        .map((source) => ({
          id: source.id,
          name: source.name,
          color: source.color
        })),
    [data, leadsEnabled]
  );

  return { eligibleSources, isLoading };
};
